<script setup lang="ts">
  import { computed, nextTick, onMounted, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { helpers } from '@vuelidate/validators'
  import FlowCardQC from '@/shared/components/FlowCardQC/FlowCardQC.vue'
  import { useCommonStore, useUserStore } from '@/shared/store'
  import { usePerformanceTiming, useValidation, useValidators } from '@/shared/composables'
  import {
    COUNTRY_CODE,
    LOAD_EVENT_NAME,
    PROCEED_EVENT_NAME,
    FLOW_CONTACTS_EMAIL_TEST_ID,
    FLOW_CONTACTS_PHONE_TEST_ID,
    FLOW_CONTACTS_PROCEED_TEST_ID,
    FLOW_CARD_TEST_ID,
  } from './FlowContactsWidgetQC.constants'
  import { TravelDirection } from '@/shared/types/common.types'
  import { AnalyticsPageName } from '@/shared/constants/analytics.constants'
  import { useGoogleAnalytics } from '@/shared/composables/useGoogleAnalytics/useGoogleAnalytics'
  import { toPolicyTimestamp } from '@qicgroupdoha/travel-devkit/helpers'
  import { useHomeUrl } from '@/shared/composables/useHomeUrl'
  import { type EmailDomain, TravelType } from '@/shared/types/api.types'
  import ButtonQC from '@qicgroupdoha/qic-connect-ds/components/ButtonQC'
  import WrapperQC from '@qicgroupdoha/qic-connect-ds/components/WrapperQC'
  import InputEmailQC from '@qicgroupdoha/qic-connect-ds/components/InputEmailQC'
  import PhoneInputQC from '@qicgroupdoha/qic-connect-ds/components/PhoneInputQC'
  import CheckboxQC from '@qicgroupdoha/qic-connect-ds/components/CheckboxQC'
  import CellQC from '@qicgroupdoha/qic-connect-ds/components/CellQC'
  import InputPromocodeQC from '@qicgroupdoha/qic-connect-ds/components/InputPromocodeQC'
  import AccordionQC from '@/shared/components/AccordionQC/AccordionQC.vue'
  import { FlowPrivacyPolicyReminderQC } from '@/entities/Flow/components/FlowPrivacyPolicyReminderQC'

  interface FormData {
    phone: string
    email: string
    promocode: string
    contactsAgree: number
    numAdults?: number
    numChildren?: number
    policyDate?: number
    endDate?: number
    travelType?: string
  }

  interface Props {
    typeOfTravel?: Exclude<TravelDirection, TravelDirection.UNKNOWN>
    withBackButton?: boolean
  }

  interface Emits {
    (e: typeof PROCEED_EVENT_NAME): void
    (
      e: typeof LOAD_EVENT_NAME,
      request: () => Promise<any>,
      requestId?: string,
      withLoader?: boolean,
      withError?: boolean
    ): void
    (e: 'back'): void
  }

  const props = withDefaults(defineProps<Props>(), {
    typeOfTravel: TravelDirection.OUTBOUND,
    withBackButton: false,
  })

  const emit = defineEmits<Emits>()
  const { getHomeUrl } = useHomeUrl()
  const homeUrl = getHomeUrl()
  const userStore = useUserStore()
  const commonStore = useCommonStore()
  const { emailValidator, phoneValidator } = useValidators()
  const gaService = useGoogleAnalytics()
  const { t, locale } = useI18n()
  const { onPerformanceCheckEnd } = usePerformanceTiming()
  const loading = ref(true)

  const isInbound = props.typeOfTravel === TravelDirection.INBOUND
  const contactsTitle = isInbound ? t('inbound.contacts.title') : t('outbound.contacts.title')

  const policyUrl = ref('')
  const phone = ref('')
  const maskedPhone = ref('')
  const phoneCompleted = ref(false)
  const email = ref('')
  const emailErrorMessage = ref('')
  const promocode = ref(userStore.promocode ?? '')
  const isAgree = ref(true)
  const countryCode = ref(userStore.countryCode ?? COUNTRY_CODE)
  const promocodeInputRef = ref<InstanceType<typeof InputPromocodeQC> | null>(null)
  const promocodeLoading = ref(false)
  const promocodeErrorMessage = ref('')
  const promocodeIsValid = ref(false)
  const emailDomains = ref<EmailDomain[]>([])
  const filteredEmailDomains = ref<EmailDomain[]>([])
  const isLoading = ref(false)

  const validationRules = computed(() => ({
    email: {
      ...emailValidator,
      errorMessage: helpers.withMessage(
        t('qic.error.email.format'),
        () => !emailErrorMessage.value
      ),
    },
    phoneCompleted: {
      ...phoneValidator,
    },
  }))

  const { v$, errors } = useValidation(validationRules.value, { email, phoneCompleted })
  const errorsCustom = ref<string[]>([])

  const validateEmail = () =>
    commonStore.validateEmail(email.value).then((message) => {
      emailErrorMessage.value = message
      return message
    })

  const resetEmailErrorMessage = () => {
    v$.value.email.$reset()
    emailErrorMessage.value = ''
  }

  const onProceed = async () => {
    v$.value.$touch()

    if (!v$.value.$error && !errorsCustom.value.length) {
      await emit(
        LOAD_EVENT_NAME,
        async () => {
          isLoading.value = true
          const message = await validateEmail()
          if (message) {
            isLoading.value = false
            return
          }

          userStore.setPromocode(promocode.value, true)
          userStore.setPhone(phone.value, true)
          userStore.setMaskedPhone(maskedPhone.value, true)
          userStore.setEmail(email.value, true)
          userStore.setCountryCode(countryCode.value, true)

          const data: FormData = {
            phone: phone.value,
            email: email.value,
            promocode: promocode.value,
            contactsAgree: isAgree.value ? 1 : 0,
          }

          const hasAllHeroSectionData =
            userStore.isHeroSection &&
            userStore.departureDate &&
            userStore.returnDate &&
            userStore.tripType &&
            userStore.adultsCount

          const hasNoHeroSectionData =
            userStore.isHeroSection &&
            (!userStore.departureDate ||
              !userStore.returnDate ||
              !userStore.tripType ||
              !userStore.adultsCount)

          if (hasAllHeroSectionData) {
            data.numAdults = userStore.adultsCount
            data.numChildren = userStore.childrenCount
            // @ts-ignore
            data.policyDate = toPolicyTimestamp(new Date(userStore.departureDate))
            // @ts-ignore
            data.endDate = toPolicyTimestamp(new Date(userStore.returnDate))
            data.travelType =
              userStore.tripType === 'SINGLE' ? TravelType.SINGLE : TravelType.MULTITRIP
          } else if (hasNoHeroSectionData) {
            return (window.location.href = homeUrl)
          }

          await commonStore.sendFormData(data)

          gaService.sendContactsProceed(
            props.typeOfTravel,
            promocode.value,
            isAgree.value,
            userStore.destination?.map((country) => country?.id || '')
          )

          emit(PROCEED_EVENT_NAME)
          isLoading.value = false
        },
        'sendFormData',
        false
      )
    }
  }

  const onEmailInput = (value: string | number | null) => {
    if (!emailDomains.value.length) return

    const domainPart = value?.toString().split('@')?.[1] || ''
    if (!domainPart.length) {
      filteredEmailDomains.value = emailDomains.value.filter((domain) => domain.level === 2)
      return
    }

    filteredEmailDomains.value = emailDomains.value
  }

  const onPromocodeValidation = async () => {
    promocodeLoading.value = true
    const result = await commonStore.validatePromocode(
      encodeURIComponent(promocode.value),
      'TRAVEL'
    )
    promocodeLoading.value = false
    promocodeErrorMessage.value = result ? '' : t('qic_promocode_invalid')
    promocodeIsValid.value = result
  }

  const onPhoneChange = (data: { unmasked: string; value: string; code: string }) => {
    if (phoneCompleted.value) {
      phone.value = `${data.code}${data.unmasked}`
    }
  }

  const fetchPolicyUrl = async () => {
    try {
      policyUrl.value = await commonStore.getPrivacyPolicyUrl()
    } catch (error) {
      console.error(error)
    }
  }

  const onPromocodeToggle = (isOpen: boolean) => {
    if (isOpen) {
      nextTick(() => {
        promocodeInputRef.value?.focus()
      })
    }
  }

  onMounted(() => {
    emit(
      LOAD_EVENT_NAME,
      async () => {
        await Promise.all([
          userStore.getFormData(props.typeOfTravel, false),
          fetchPolicyUrl(),
          commonStore.getEmailDomains(),
        ])
        emailDomains.value = commonStore.emailDomains || []
        phone.value = userStore.phone ?? ''
        maskedPhone.value = userStore.maskedPhone ?? ''
        email.value = userStore.email ?? ''
        loading.value = false

        onPerformanceCheckEnd()
        gaService.sendViewPage(props.typeOfTravel, AnalyticsPageName.CONTACTS)
      },
      'contacts',
      true,
      false
    )
  })
</script>

<template>
  <FlowCardQC
    :title="contactsTitle"
    class="flow-contacts"
    :with-back-button="withBackButton"
    :data-test="FLOW_CARD_TEST_ID"
    @back="emit('back')"
  >
    <WrapperQC v-if="!loading" direction="column" gap="var(--spacing-5)">
      <InputEmailQC
        v-model="email"
        :label="$t('outbound.travelers.email')"
        :errors="errors.email"
        :is-filled="!v$.email.$invalid"
        :domains-list="filteredEmailDomains"
        :loading="isLoading"
        :data-test="FLOW_CONTACTS_EMAIL_TEST_ID"
        @input="onEmailInput"
        @focus="resetEmailErrorMessage"
      />

      <PhoneInputQC
        v-model="maskedPhone"
        v-model:country-iso.uppercase="countryCode"
        :errors="errors.phoneCompleted"
        :loading="isLoading"
        :data-test="FLOW_CONTACTS_PHONE_TEST_ID"
        @completed="phoneCompleted = $event"
        @focus="v$.phoneCompleted.$reset()"
        @change="onPhoneChange"
      />
    </WrapperQC>

    <AccordionQC
      class="contacts-promocode"
      icon-position="end"
      :style="{ pointerEvents: isLoading ? 'none' : 'initial' }"
      @toggle="onPromocodeToggle"
    >
      <template #header>
        <p class="heading-6">{{ $t('qic_have_promocode') }}</p>
      </template>

      <template #content>
        <InputPromocodeQC
          ref="promocodeInputRef"
          v-model="promocode"
          :label="$t('qic_promocode')"
          :is-valid="promocodeIsValid"
          :is-filled="!promocodeErrorMessage && promocode.length > 0"
          :errors="promocodeErrorMessage"
          :loading="promocodeLoading"
          :container-attrs="{ 'data-test': 'contacts-promocode-input' }"
          data-test="contacts-promocode-input-inner"
          @focus="promocodeErrorMessage = ''"
          @send-promocode="onPromocodeValidation"
        />
      </template>
    </AccordionQC>

    <template #button>
      <CellQC class="flow-contacts__agree-cell" padding-block="spacing-3">
        <template #title>
          <CheckboxQC
            id="agreed"
            v-model="isAgree"
            name="agreed"
            :disabled="isLoading"
            class="flow-contacts__agreed"
            :container-attrs="{ 'data-test': 'contacts-agree-checkbox-container' }"
            data-test="contacts-agree-checkbox-inner"
          >
            <p class="text-medium">{{ $t('qic_user_contacts_acceptance') }}</p>
          </CheckboxQC>
        </template>
      </CellQC>

      <ButtonQC
        wide
        :data-test="FLOW_CONTACTS_PROCEED_TEST_ID"
        :icon-after-content="`arrow-narrow-${locale === 'en' ? 'right' : 'left'}`"
        :loading="isLoading"
        @click="onProceed"
      >
        {{ $t('travel.common.continue.button') }}
      </ButtonQC>

      <FlowPrivacyPolicyReminderQC data-test="contacts-privacy-policy-reminder" />
    </template>
  </FlowCardQC>
</template>

<style scoped lang="scss">
  .flow-contacts__agree-cell {
    :deep(.ds-base-checkbox__input + label) {
      align-items: start;
    }
  }
</style>

<style lang="scss">
  .contacts-promocode {
    border-radius: var(--radius-5);
    border: 1px solid var(--colorBrdrSrfPrimary);

    .accordion-qc__header {
      justify-content: space-between;
      padding-block: calc(var(--spacing-2) + var(--spacing-7));
      padding-inline: var(--spacing-7);
    }

    .accordion-qc__content {
      padding: var(--spacing-7);
      border-top: 1px solid var(--colorBrdrSrfTertiary);
    }
  }
</style>
